(function () {
  if (window.__cartRewardsProgressManager) return;

  const instances = new Set();
  const cartMutationPattern = /\/cart\/(add|change|update|clear)(\.js)?(?:[?#]|$)/i;
  let refreshTimer;
  let refreshRequest;

  function formatMoney(cents, format) {
    const amount = Number(cents) || 0;
    const formatString = format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      const fixedNumber = (number / 100).toFixed(precision);
      const parts = fixedNumber.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return `${whole}${parts[1] ? `${decimal}${parts[1]}` : ''}`;
    }

    const match = formatString.match(placeholderRegex);
    const token = match ? match[1] : 'amount';
    let value;

    switch (token) {
      case 'amount_no_decimals': value = withDelimiters(amount, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(amount, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(amount, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(amount, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(amount, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(amount, 2, "'", '.'); break;
      default: value = withDelimiters(amount, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function cartSubtotal(cart) {
    const subtotal = Number(cart?.items_subtotal_price);
    if (Number.isFinite(subtotal)) {
      const giftTotal = Array.isArray(cart?.items)
        ? cart.items.reduce((total, item) => {
            return item?.properties?._cart_reward_gift === 'true'
              ? total + (Number(item.final_line_price) || 0)
              : total;
          }, 0)
        : 0;
      return Math.max(subtotal - giftTotal, 0);
    }
    return Number(cart?.total_price) || 0;
  }

  function updateAll(cart) {
    const subtotal = typeof cart === 'number' ? cart : cartSubtotal(cart);
    instances.forEach((bar) => {
      if (bar.isConnected) bar.paint(subtotal);
    });
  }

  function getCartUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart.js`;
  }

  function refreshCart() {
    if (refreshRequest) return refreshRequest;

    refreshRequest = fetch(getCartUrl(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        updateAll(cart);
        return cart;
      })
      .catch(() => null)
      .finally(() => { refreshRequest = null; });

    return refreshRequest;
  }

  function scheduleRefresh(delay = 60) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refreshCart, delay);
  }

  class CartRewardsProgress extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.addMoreTemplate = this.dataset.addMoreTemplate || 'Buy [amount] more for [reward]';
      this.unlockedTemplate = this.dataset.unlockedTemplate || '[reward] unlocked!';
      this.allUnlocked = this.dataset.allUnlocked || 'All rewards unlocked!';
      this.fill = this.querySelector('[data-crp-fill]');
      this.message = this.querySelector('[data-crp-message]');
      this.status = this.querySelector('[data-crp-status]');
      this.track = this.querySelector('[role="progressbar"]');
      this.milestones = Array.from(this.querySelectorAll('.crp__milestone'))
        .map((node, index) => ({
          node,
          threshold: Number(node.dataset.threshold) || 0,
          label: index === 0 ? this.dataset.shippingLabel : index === 1 ? this.dataset.secondLabel : this.dataset.thirdLabel,
        }))
        .filter((milestone) => milestone.threshold > 0)
        .sort((a, b) => a.threshold - b.threshold);

      instances.add(this);
      scheduleRefresh(0);
    }

    disconnectedCallback() {
      instances.delete(this);
    }

    paint(subtotal) {
      const total = Number(subtotal) || 0;
      const highest = this.milestones.length ? this.milestones[this.milestones.length - 1].threshold : 0;
      const progress = highest > 0 ? Math.min((total / highest) * 100, 100) : 0;

      if (this.fill) this.fill.style.width = `${progress.toFixed(2)}%`;
      if (this.track) this.track.setAttribute('aria-valuenow', String(Math.round(progress)));

      let latestUnlocked = null;
      let nextReward = null;

      this.milestones.forEach((milestone) => {
        const reached = total >= milestone.threshold;
        milestone.node.classList.toggle('is-reached', reached);
        if (reached) latestUnlocked = milestone;
        if (!reached && !nextReward) nextReward = milestone;
      });

      if (this.message) {
        if (nextReward) {
          const remaining = Math.max(nextReward.threshold - total, 0);
          this.message.textContent = this.addMoreTemplate
            .replace('[amount]', formatMoney(remaining, this.moneyFormat))
            .replace('[reward]', nextReward.label || 'reward');
        } else {
          this.message.textContent = this.allUnlocked;
        }
      }

      if (this.status) {
        this.status.classList.toggle('is-hidden', !latestUnlocked);
        this.status.textContent = latestUnlocked
          ? this.unlockedTemplate.replace('[reward]', latestUnlocked.label || 'Reward')
          : '';
      }
    }
  }

  if (!customElements.get('cart-rewards-progress')) {
    customElements.define('cart-rewards-progress', CartRewardsProgress);
  }

  function handleCartUpdate(event) {
    const cartData = event?.cartData || event?.detail?.cart || event?.detail;
    if (cartData && (cartData.items_subtotal_price !== undefined || cartData.total_price !== undefined)) {
      updateAll(cartData);
    } else {
      scheduleRefresh();
    }
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
  }

  ['cart:refresh', 'cart:updated', 'cart:change', 'free-shipping:refresh'].forEach((eventName) => {
    document.addEventListener(eventName, handleCartUpdate);
  });

  const nativeFetch = window.fetch.bind(window);
  window.fetch = function (...args) {
    const request = args[0];
    const requestUrl = typeof request === 'string' ? request : request?.url;
    const isCartMutation = typeof requestUrl === 'string' && cartMutationPattern.test(requestUrl);
    const requestPromise = nativeFetch(...args);
    if (isCartMutation) {
      requestPromise.then((response) => response.ok && scheduleRefresh()).catch(() => null);
    }
    return requestPromise;
  };

  const nativeXhrOpen = XMLHttpRequest.prototype.open;
  const nativeXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, ...args) {
    this.__isCartRewardsMutation = typeof url === 'string' && cartMutationPattern.test(url);
    return nativeXhrOpen.call(this, method, url, ...args);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (this.__isCartRewardsMutation) {
      this.addEventListener('loadend', () => {
        if (this.status >= 200 && this.status < 400) scheduleRefresh();
      }, { once: true });
    }
    return nativeXhrSend.apply(this, args);
  };

  window.__cartRewardsProgressManager = { refresh: scheduleRefresh, update: updateAll };
})();
